import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { components } from '../schema/backend';
import { OauthService } from './oauth.service';
import { UserSettingsService } from './user-settings.service';
import { RepoService } from './repo.service';
import { ApiService } from './api.service';
import { FileSystemService } from './file-system.service';
import { lastValueFrom } from 'rxjs';

export type ChatRequest = components['schemas']['ChatRequest'];
export type Message = components['schemas']['Message'];

export type AiEvent = {
  action: 'open' | 'close' | 'view';
  section: 'file' | 'shell' | 'mcp' | 'answer' | 'question' | 'message';
  content: string | null;
  path: string | null;
};

export type WritingChunk = {
  content: string;
  ai_write_section: string | null;
  event: AiEvent | null;
};

@Injectable({
  providedIn: 'root',
})
export class StreamService {

  constructor(
    private auth: OauthService,
    private userSettings: UserSettingsService,
    private api: ApiService,
    private fs: FileSystemService,
    private repoService: RepoService,
  ) {}

  private trimTreeToDepth(tree: string, maxDepth: number = 2): string {
    const lines = tree.split('\n');
    const output: string[] = [];
    let inDirectories = false;
    let inFiles = false;

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) {
        if (output.length > 0 && !inDirectories && !inFiles) {
          output.push('');
        }
        continue;
      }

      if (trimmed === 'Directories:' || trimmed === 'Files:') {
        inDirectories = trimmed === 'Directories:';
        inFiles = trimmed === 'Files:';
        output.push(trimmed);
        continue;
      }

      if (trimmed.startsWith('Contents of directory')) {
        output.push(trimmed);
        continue;
      }

      if (trimmed.startsWith('📁 ') || trimmed.startsWith('📄 ')) {
        const pathPart = trimmed.replace(/^📁\s|^📄\s/, '').replace(/\/$/, '');
        const segments = pathPart.split(/[\\/]/).filter(Boolean);
        const depth = segments.length;

        if (depth <= maxDepth + 1) {
          output.push(line);
        }
      }
    }

    return output.join('\n');
  }

  async getAssistantStream(
    req: Message[],
    endpoint: string = '/chat',
    token: string | null = null,
    session_label: string | null = null,
  ): Promise<AsyncIterable<WritingChunk>> {
    const userInfo = await this.auth.getUser();
    if (!userInfo) {
      throw new Error('user not signed in');
    }
    const rawTree = await lastValueFrom(this.api.treeString('.'));
    const details: ChatRequest = {
      history: JSON.parse(JSON.stringify(req)),
      tools: JSON.parse(localStorage.getItem('mcpTools')!).tools,
      tree: this.trimTreeToDepth(rawTree, 2),
      session_label: session_label,
      root_folder: `/${this.fs.rootNode.name}`,
      instructions: this.userSettings.savedInstructions,
      repo_id: String(this.repoService.repo()?.id!),
    };
    const stream = {
      async *[Symbol.asyncIterator]() {
        const url = `${environment.backendUrl}${endpoint}`;
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token || ''}`,
          },
          body: JSON.stringify(details),
        });

        if (!response.body) {
          throw new Error('Response body is null');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        // A read can end mid-line (a long answer chunk spans several network
        // reads); carry the unfinished tail into the next read instead of
        // JSON-parsing half a line, which aborted the stream before the closing
        // tag and close event arrived.
        let buffer = '';
        try {
          while (true) {
            const { done, value } = await reader.read();
            buffer += decoder.decode(value, { stream: !done });
            const lines = buffer.split('\n');
            buffer = done ? '' : lines.pop()!;
            for (const line of lines) {
              if (line.trim()) {
                const json = JSON.parse(line);
                if ('error' in json) {
                  throw json.error;
                }
                yield json;
              }
            }
            if (done) break;
          }
        } finally {
          reader.releaseLock();
        }
      },
    };

    return stream;
  }

//   async gatherDetails(): Promise<ChatRequest> {
//     const userInfo = await this.auth.getUser();
//     if (!userInfo) {
//       throw new Error('user not signed in');
//     }
//     // Deep copy history to avoid mutation issues
//     const details: ChatRequest = {
//       history: JSON.parse(JSON.stringify(this.chatComponent.history())),
//       provider: this.userSettings.selectedProvider,
//       user_id: userInfo.user_id,
//       tools: (await this.mcpService.client.listTools()).tools,
//       tree: await lastValueFrom(this.api.treeString('.')),
//       session_label: this.aiService.session_label,
//       root_folder: `/${this.fs.rootNode.name}`,
//       instructions: this.userSettings.savedInstructions,
//       repo_id: String(this.repoService.repo()?.id!), // TODO: replace with actual repo id
//     };
//     // console.log('tree', details.tree);
//     return details;
//   }
}
